// `??` so an intentionally-empty VITE_API_ENDPOINT (staging/preview, worker
// proxies API same-origin) stays "" → relative URLs, not localhost.
const API_BASE_URL = import.meta.env.VITE_API_ENDPOINT ?? 'http://localhost:4200';

export interface SignupOptions {
  signupEnabled: boolean;
  trialDays: number;
  plans: Array<{ code: string; name: string; includedSeats: number }>;
}

export interface SignupRequestBody {
  email: string;
  name: string;
  company: string;
  country: string;
  period: 'MONTHLY' | 'YEARLY';
  /** The company's own site, optional. Not `website`, which is the hidden bot trap. */
  companyWebsite?: string;
  website?: string;
}

export interface SignupResult {
  tenantId: string;
  email: string;
  existingAccount: boolean;
  card: { clientSecret: string; publishableKey: string; stripeAccountId: string | null } | null;
  cardUnavailable: boolean;
  trialDays: number;
}

/** What the API said when it refused, in words a visitor can act on. */
export class SignupApiError extends Error {}

async function call<T>(path: string, init?: RequestInit): Promise<T> {
  let response: Response;
  try {
    response = await fetch(`${API_BASE_URL}/api/v1/signup/${path}`, {
      ...init,
      headers: { 'Content-Type': 'application/json' },
    });
  } catch {
    throw new SignupApiError('We could not reach Revvly. Check your connection and try again.');
  }
  if (response.ok) return (await response.json().catch(() => ({}))) as T;
  if (response.status === 429) {
    throw new SignupApiError('Too many attempts. Wait a minute and try again.');
  }
  const body = (await response.json().catch(() => null)) as { message?: string | string[] } | null;
  const message = Array.isArray(body?.message) ? body?.message[0] : body?.message;
  throw new SignupApiError(
    message && response.status < 500
      ? message
      : 'Signup is unavailable right now. Try again later.',
  );
}

export const fetchSignupOptions = () => call<SignupOptions>('options');

export const requestSignup = (body: SignupRequestBody) =>
  call<{ ok: true }>('request', { method: 'POST', body: JSON.stringify(body) });

export const confirmSignup = (token: string, password: string) =>
  call<SignupResult>('confirm', { method: 'POST', body: JSON.stringify({ token, password }) });
